import type { SystemStyleObject } from '@mui/system';
import type { Theme } from '@mui/material/styles';
import { form, radius, typography } from '@/shared/theme/tokens';

/** Label rendered above the control, separate from the input outline. */
export const fieldLabelSx: SystemStyleObject<Theme> = {
  display: 'block',
  fontSize: form.labelFontSize,
  fontWeight: typography.weight.medium,
  lineHeight: 1.4,
  color: 'text.primary',
};

/**
 * Wrapper-only styles — height/fontSize from Theme
 * (`MuiOutlinedInput` + `MuiPickersOutlinedInput`).
 */
export const outlinedFieldSx: SystemStyleObject<Theme> = {
  '& .MuiOutlinedInput-root, & .MuiPickersOutlinedInput-root': {
    bgcolor: 'background.paper',
  },
  '& .MuiSelect-select': {
    minHeight: 'unset',
    display: 'flex',
    alignItems: 'center',
  },
};

/**
 * MUI Autocomplete adds extra padding on inputRoot/input that fights
 * `form.controlHeight` — placeholder and popup icon sit off-center.
 */
export const autocompleteFieldSx: SystemStyleObject<Theme> = {
  '& .MuiAutocomplete-inputRoot': {
    alignItems: 'center',
    minHeight: form.controlHeight,
    height: 'auto',
    py: 0,
  },
  '& .MuiAutocomplete-input': {
    py: '0 !important',
  },
  '& .MuiAutocomplete-endAdornment': {
    top: '50%',
    transform: 'translateY(-50%)',
  },
  '& .MuiAutocomplete-tag': {
    my: 0,
    borderRadius: radius.sm,
  },
};

/**
 * Toolbar search at `form.controlHeightCompact` (32px).
 * `&&` beats the theme rule that pins every outlined input to 36px.
 * Vertical padding is cleared so the placeholder stays centered in the shorter box.
 * Left padding is cleared because the search icon already separates the text from the outline.
 */
export const searchFieldSx: SystemStyleObject<Theme> = {
  '&& .MuiOutlinedInput-root': {
    height: form.controlHeightCompact,
    minHeight: form.controlHeightCompact,
  },
  '&& .MuiOutlinedInput-input': {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
  },
};

/**
 * Outlined Select at `form.controlHeightCompact` (32px).
 * `&&` beats the theme rule that pins every outlined input to 36px.
 * MUI Select already uses `variant` for outlined/filled/standard, so this is opt-in via `density`.
 */
export const outlinedSelectCompactSx: SystemStyleObject<Theme> = {
  '&&': {
    height: form.controlHeightCompact,
    minHeight: form.controlHeightCompact,
  },
  '& .MuiSelect-select': {
    paddingTop: 0,
    paddingBottom: 0,
  },
};

/** Single-line compact field. Roots that already show tags keep `height: auto`. */
export const autocompleteFieldCompactSx: SystemStyleObject<Theme> = {
  '& .MuiAutocomplete-inputRoot': {
    minHeight: form.controlHeightCompact,
  },
  '& .MuiAutocomplete-inputRoot:not(:has(.MuiAutocomplete-tag))': {
    height: form.controlHeightCompact,
  },
};

export const formControlSx: SystemStyleObject<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
};

/** Live `current / max` on the right of an external field label. */
export const characterCountLabelSx: SystemStyleObject<Theme> = {
  color: 'currentColor',
  opacity: 0.42,
  fontSize: form.labelFontSize,
  fontWeight: typography.weight.regular,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

/** Match MUI InputBase `::placeholder` (currentColor + 0.42 opacity), not `text.secondary`. */
export const selectPlaceholderSx: SystemStyleObject<Theme> = {
  color: 'currentColor',
  opacity: 0.42,
  font: 'inherit',
  lineHeight: 'inherit',
};

export const selectMenuPaperSx: SystemStyleObject<Theme> = {
  mt: form.menuPaperMarginTop,
  borderRadius: radius.sm,
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  boxShadow: form.menuPaperElevation,
  maxHeight: form.menuMaxHeight,
};

export const selectMenuItemSx: SystemStyleObject<Theme> = {
  fontSize: form.fontSize,
  minHeight: form.menuItemMinHeight,
  py: form.menuItemPaddingY,
  px: form.menuItemPaddingX,
};

/**
 * Phone field: +84 is a full-height gray cap on the outline.
 * Root padding and the adornment's 8px margin are cleared so the cap
 * sits on the border and the number keeps only `form.inputPaddingX`.
 * `&&` beats MUI's adorned-start padding and margin.
 */
export const phoneFieldSx = (theme: Theme): SystemStyleObject<Theme> => ({
  '&& .MuiOutlinedInput-root': {
    paddingLeft: 0,
    overflow: 'hidden',
  },
  '&& .MuiInputAdornment-positionStart': {
    alignSelf: 'stretch',
    maxHeight: 'none',
    marginRight: 0,
    px: form.inputPaddingX,
    bgcolor: theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800],
    color: 'text.primary',
    borderRight: 1,
    borderColor: 'divider',
  },
  '&& .MuiOutlinedInput-input': {
    paddingLeft: form.inputPaddingX,
  },
});
